import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { config } from "../app-env/common";
import type { Route, RouteContext } from "../router";
import { centeredColumn } from "../van/centered-column";
import { button, div, input, label, p, span } from "../van/van-tags";

export class Sp2FactoryPage implements Route {
	async #listFiles(): Promise<void> {
		this.#reactive.helpText = "";

		const req = this.#ctx.api.get("/firmwares/smartplug2");
		req.setSignedInUserAuth();

		const res = await req.do();
		if (res.ok) {
			this.#reactive.filenames = await res.json();
		} else {
			this.#reactive.filenames = [];
			this.#reactive.helpText = await res.text();
		}
	}

	#onChangeSelectedFile(e: Event): void {
		if (!(e.target instanceof HTMLInputElement)) return;
		if (!e.target.files?.length) return;
		if (e.target.files.length < 1) return;
		this.#selectedFile = e.target.files[0];
		this.#reactive.helpText = "";
	}

	async #uploadFile(): Promise<void> {
		this.#reactive.helpText = "";
		if (!this.#selectedFile) {
			this.#reactive.helpText = "you must select a file first";
			return;
		}

		const req = this.#ctx.api.put(
			`/firmwares/smartplug2/${this.#selectedFile.name}`,
		);
		req.setSignedInUserAuth();
		req.setBody(this.#selectedFile);

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			this.#reactive.filenames = await res.json();
			this.#reactive.helpText = "Ok";
		} else {
			this.#reactive.helpText = await res.text();
		}
	}

	async refresh(): Promise<void> {
		await this.#listFiles();
	}

	#ctx: RouteContext;

	#deviceBox: ChildDom = () =>
		div(
			{ class: "box" },
			div(
				{ class: "field" },
				label({ class: "label" }, "IMEI"),
				div(
					{ class: "field has-addons" },
					div(
						{ class: "control is-expanded" },
						input({
							class: "input",
							onchange: (e: Event) => {
								if (!(e.target instanceof HTMLInputElement))
									return;
								this.#imei = e.target.value;
							},
							type: "text",
						}),
					),
					div(
						{ class: "control" },
						button({ class: "button is-primary" }, "Clear"),
					),
				),
			),
			div(
				{ class: "buttons" },
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () =>
							this.#ctx.router.jump(
								`/smartplug2/${this.#imei}/provision`,
							),
					},
					"Provision",
				),
				button(
					{
						class: "button is-primary is-fullwidth",
						onclick: () =>
							this.#ctx.router.jump(
								`/smartplug2/${this.#imei}/commands`,
							),
					},
					"Send Command",
				),
			),
		);

	#firmwareBox: ChildDom = () =>
		div(
			{
				class: "box",
			},
			div(
				{ class: "field" },
				label({ class: "label" }, "Stored files"),
				() =>
					div(
						{ class: "tags" },
						this.#reactive.filenames.map((filename) =>
							span({ class: "tag" }, filename),
						),
					),
				p(
					{ class: "help" },
					`Let's Encrypt CA: https://${config.sp2gwHost}/firmwares/{filename}`,
				),
				p(
					{ class: "help" },
					`Self-signed CA: https://${config.sp2gwSelfSignedHost}/firmwares/{filename}`,
				),
			),
			div(
				{ class: "field" },
				label({ class: "label" }, "Select file to upload"),
				div(
					{ class: "field has-addons" },
					div(
						{ class: "control is-expanded" },
						input({
							class: "input",
							onchange: (e: Event) =>
								this.#onChangeSelectedFile(e),
							type: "file",
						}),
					),
					div(
						{ class: "control" },
						button(
							{
								class: "button is-primary",
								onclick: () => this.#uploadFile(),
							},
							"Upload",
						),
					),
				),
				p(
					{
						class: () =>
							this.#reactive.helpText === "Ok"
								? "help is-success"
								: "help is-danger",
					},
					() => this.#reactive.helpText,
				),
			),
		);

	#imei = "";

	#reactive = reactive({
		filenames: [""],
		helpText: "",
	});

	#selectedFile: File | undefined;

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredColumn(
			{
				size: "narrow",
			},
			this.#deviceBox,
			this.#firmwareBox,
		);
	}
}
